// MorrowOS Agents — client helpers for the persistent, autonomous role-holders.
//
//   GET  /api/morrow/agents            -> { agents: Agent[] }
//   POST /api/morrow/agents            -> { ok, id, homeThreadId }
//   POST /api/morrow/agents/update     -> { ok }
//   POST /api/morrow/agents/status     -> { ok, status }
//   POST /api/morrow/agents/delete     -> { ok }
//   POST /api/morrow/agents/run        -> { ok, started }
//   GET  /api/morrow/agents/runs       -> { runs: AgentRun[] }
//   POST /api/morrow/agents/tags       -> { ok }
//   POST /api/morrow/agents/tags/delete-> { ok }
//
// The owner is derived server-side from the VERIFIED Clerk token, so no user id
// is sent. Every call is non-throwing — failures resolve to null/[] so the UI
// keeps rendering. Mirrors src/orgMembers.ts.
import { apiUrl } from "./apiBase";
import { cached, invalidateCache } from "./dataCache";
import { withMockAgentRuns, withMockAgents } from "./components/workspace/agentsMock";
import { isAgentsMockMode } from "./components/workspace/agentsMockMode";
import { getClerkToken, getOwnerId } from "./clerkTokenBridge";
import { openUpgradeModal, parseGateUpgrade } from "./upgradeModal";

async function reqHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
  const headers: Record<string, string> = { accept: "application/json", ...extra };
  const token = await getClerkToken().catch(() => null);
  if (token) headers["x-clerk-token"] = token;
  const ownerId = getOwnerId();
  if (ownerId) headers["x-morrow-owner-id"] = ownerId;
  return headers;
}

/** Drop the cached agent reads — called by every write in this module. */
export function invalidateAgents(): void {
  invalidateCache("/api/morrow/agents");
}

export type AgentAutonomy = "read" | "approval" | "autonomous";
export type AgentStatus = "draft" | "active" | "paused" | "archived";
export type AgentIdentityKind = "managed" | "gmail";

export interface AgentTag {
  readonly handle: string;
  readonly channelScope: string;
}

export interface AgentBudget {
  readonly tokens?: number;
  readonly browserHours?: number;
  readonly money?: number;
  /** Daily hands-on-time ceiling in SECONDS — the cost governor's per-agent cap for the
   * "keep working" loop. The create form sets it from a minutes/hours input. */
  readonly dailyActiveSeconds?: number;
}

export interface Agent {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly description: string;
  readonly avatar: string | null;
  readonly voice: string | null;
  readonly instruction: string;
  readonly identityKind: AgentIdentityKind;
  readonly identityAddress: string | null;
  readonly allowedApps: readonly string[] | null;
  readonly capabilities: Record<string, unknown> | null;
  readonly mayTaskAgents: readonly string[] | null;
  readonly autonomy: AgentAutonomy;
  readonly limits: Record<string, unknown> | null;
  readonly budget: AgentBudget | null;
  readonly triggers: Record<string, unknown> | null;
  readonly scheduleCron: string | null;
  /** 1 = "Keep working" (proactive self-continuation) is ON for this agent; 0 = reactive. */
  /** When it next wakes on its own (ISO), or null. */
  readonly nextRunAt?: string | null;
  readonly keepWorking: number;
  readonly status: AgentStatus;
  readonly charterVersion: number;
  readonly homeThreadId: string | null;
  readonly tags: readonly AgentTag[];
  readonly updatedAt: string;
}

/** The writable fields for create/update (a partial for update). */
export interface AgentInput {
  readonly name?: string;
  readonly role?: string;
  readonly description?: string;
  /** Chosen mascot: an emoji, or an uploaded image URL / data-URI. */
  readonly avatar?: string | null;
  readonly instruction?: string;
  readonly identityKind?: AgentIdentityKind;
  readonly identityAddress?: string | null;
  readonly allowedApps?: readonly string[] | null;
  readonly capabilities?: Record<string, unknown> | null;
  readonly mayTaskAgents?: readonly string[] | null;
  readonly autonomy?: AgentAutonomy;
  readonly limits?: Record<string, unknown> | null;
  readonly budget?: AgentBudget | null;
  readonly triggers?: Record<string, unknown> | null;
  readonly scheduleCron?: string | null;
  readonly status?: AgentStatus;
}

export interface AgentRun {
  readonly id: string;
  readonly agentId: string;
  readonly source: string;
  readonly detail: string | null;
  readonly status: string;
  readonly error: string | null;
  readonly threadId: string | null;
  readonly createdAt: string;
}

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), { headers: await reqHeaders() });
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}

async function postJson<T = unknown>(
  path: string,
  body: Record<string, unknown>,
): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: await reqHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(body),
    });
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}

/**
 * Like `postJson`, but a 402 (plan gate) pops the upgrade modal from the encoded
 * gate marker in the body before returning null. Use for gated WRITES (create
 * agent, etc.) so hitting a plan cap surfaces the paywall instead of failing
 * silently. Returns null on any non-2xx (the caller treats it as "not created").
 */
async function postJsonGated<T = unknown>(
  path: string,
  body: Record<string, unknown>,
): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: await reqHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(body),
    });
    if (res.status === 402) {
      const gateBody = (await res.json().catch(() => null)) as { error?: string } | null;
      const gate = parseGateUpgrade(gateBody?.error);
      if (gate) {
        openUpgradeModal({
          capability: gate.capability,
          message: gate.message,
          reason: gate.reason,
        });
      }
      return null;
    }
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}

// Module-level last-good caches. Pages seed their initial state from these so
// navigating back to Agents / an agent window shows data INSTANTLY (no loading-flash,
// no backend hammer on every mount) and revalidate in the background. Only real
// responses are cached — a failed (null) fetch keeps the previous good value rather
// than clobbering it with an empty list.
let agentsCache: readonly Agent[] | null = null;
let agentRunsCache: readonly AgentRun[] | null = null;
export const cachedAgents = (): readonly Agent[] | null => agentsCache;
export const cachedAgentRuns = (): readonly AgentRun[] | null => agentRunsCache;
// Same last-good cache for the three feeds the Activities page depends on. Without this, a
// single transient fetch/auth blip (e.g. a Clerk-token race on mount) returned [] and blanked
// the whole Activities page — the "approvals/flows are happening but my activity is empty" bug.
// On a null (failed) fetch we KEEP the previous good value instead of clobbering it with [].
let workflowRunsCache: readonly WorkflowRunLite[] | null = null;
let allowancesCache: readonly AllowanceLite[] | null = null;
let agentBoardCache: readonly AgentBoardMessage[] | null = null;

export async function fetchAgents(): Promise<readonly Agent[]> {
  // Cached: the agents list is read on every panel mount, and nothing about it changes between
  // two renders a second apart. Mutations call `invalidateAgents()`.
  const res = await cached("/api/morrow/agents", () =>
    getJson<{ agents?: Agent[] }>("/api/morrow/agents"),
  );
  const mockMode = isAgentsMockMode();
  if (res) agentsCache = withMockAgents(res.agents ?? [], mockMode);
  else if (agentsCache === null && mockMode) agentsCache = withMockAgents([], mockMode);
  return agentsCache ?? [];
}

export async function createAgent(
  input: AgentInput & { readonly tags?: readonly AgentTag[] },
): Promise<{ id: string; homeThreadId: string | null } | null> {
  invalidateAgents();
  const res = await postJsonGated<{ ok?: boolean; id?: string; homeThreadId?: string | null }>(
    "/api/morrow/agents",
    input as Record<string, unknown>,
  );
  return res?.id ? { id: res.id, homeThreadId: res.homeThreadId ?? null } : null;
}

export async function updateAgent(id: string, input: AgentInput): Promise<boolean> {
  invalidateAgents();
  const res = await postJson<{ ok?: boolean }>("/api/morrow/agents/update", {
    id,
    ...input,
  });
  return Boolean(res?.ok);
}

export async function setAgentStatus(id: string, status: AgentStatus): Promise<boolean> {
  invalidateAgents();
  const res = await postJson<{ ok?: boolean }>("/api/morrow/agents/status", { id, status });
  return Boolean(res?.ok);
}

/** Flip the "Keep working" (proactive self-continuation) opt-in for an agent. Arming the loop
 * server-side only takes effect when continuous execution is enabled; until then the flag is
 * just stored. Non-throwing (returns false on failure). */
export async function setAgentKeepWorking(id: string, on: boolean): Promise<boolean> {
  invalidateAgents();
  // Gated: turning keep-working ON requires agent.proactive (Ultra+). Use postJsonGated so a
  // 402 pops the unlock modal instead of a silent dead toggle. (Turning OFF is never gated.)
  const res = await postJsonGated<{ ok?: boolean }>("/api/morrow/agents/keep-working", { id, on });
  return Boolean(res?.ok);
}

export async function deleteAgent(id: string): Promise<boolean> {
  invalidateAgents();
  const res = await postJson<{ ok?: boolean }>("/api/morrow/agents/delete", { id });
  return Boolean(res?.ok);
}

export async function runAgent(id: string, task?: string): Promise<boolean> {
  const res = await postJson<{ ok?: boolean; started?: boolean }>("/api/morrow/agents/run", {
    id,
    ...(task ? { task } : {}),
  });
  return Boolean(res?.started);
}

export interface AgentBrowserActionResult {
  readonly type: string;
  readonly ok: boolean;
  readonly text?: string;
  readonly imageBase64?: string;
  readonly url?: string;
  readonly title?: string;
  readonly liveViewUrl?: string;
}
export interface AgentBrowserResult {
  readonly ok: boolean;
  readonly sessionId: string | null;
  readonly results: readonly AgentBrowserActionResult[];
  readonly error?: string;
}

/** Drive the agent's live cloud browser (owner, from the UI). Actions e.g.
 * [{type:"navigate",url},{type:"screenshot"}] or [{type:"clickAt",x,y},{type:"screenshot"}]. */
export async function runAgentBrowser(
  agentId: string,
  actions: ReadonlyArray<Record<string, unknown>>,
): Promise<AgentBrowserResult | null> {
  return postJson<AgentBrowserResult>("/api/morrow/agents/browser", { agentId, actions });
}

/** Result of opening a Browser Use managed browser (Option B: residential-IP live browser). */
export interface BrowserUseOpenResult {
  readonly ok: boolean;
  readonly sessionId?: string;
  readonly liveUrl?: string;
  readonly error?: string;
}

/**
 * Open a Browser Use residential-IP live browser (Option B). SPENDS money the moment it
 * succeeds ($0.02/browser-hr + residential-proxy egress). Pass the panel's pixel size so the
 * browser boots at that viewport (no letterbox in the live view). Returns { ok:false } (503)
 * when the key isn't configured.
 */
export async function openBrowserUse(size?: {
  width: number;
  height: number;
  /** HARD restart — stop the persisted session and spin a brand-new one (don't reuse a dead one). */
  fresh?: boolean;
}): Promise<BrowserUseOpenResult | null> {
  return postJson<BrowserUseOpenResult>("/api/morrow/browser-use/open", size ?? {});
}

/** Stop a Browser Use browser — the ONLY way to end its billing. Always call when done. */
export async function stopBrowserUse(sessionId: string): Promise<boolean> {
  const res = await postJson<{ ok?: boolean }>("/api/morrow/browser-use/stop", { sessionId });
  return Boolean(res?.ok);
}

/**
 * Is a Browser Use session still alive? A cheap metadata check (NOT a new browser) the live
 * view polls so it can replace a dead/expired session's stale iframe with our own restart UI,
 * instead of leaving Browser Use's cross-origin "Connection Lost" page showing.
 */
export async function browserUseStatus(sessionId: string): Promise<boolean> {
  const res = await getJson<{ ok?: boolean; active?: boolean }>(
    `/api/morrow/browser-use/status?sessionId=${encodeURIComponent(sessionId)}`,
  );
  // No answer (offline, server hiccup) is NOT "ended" — treating it so dropped a live, still-billing
  // session from view on a wifi blip. Only an explicit `active: false` ends it.
  if (!res) return true;
  return res.active !== false;
}

/** A connectable Composio toolkit (integration) the agent can be allowed to use. */
export interface Toolkit {
  readonly slug: string;
  readonly name: string;
  readonly logo?: string;
}

/** List connectable toolkits for the settings picker (searchable). `q` filters server-side. */
export async function fetchToolkits(q?: string): Promise<readonly Toolkit[]> {
  const res = await getJson<{ toolkits?: Toolkit[] }>(
    `/api/morrow/composio/toolkits?limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}`,
  );
  return res?.toolkits ?? [];
}

/** The agent's CURRENT live browser (the one its cloud_browser tool drives), if any. */
export interface AgentBrowserLive {
  readonly ok: boolean;
  readonly live: boolean;
  readonly sessionId?: string;
  readonly liveUrl?: string;
  readonly status?: string;
}

/**
 * Fetch the agent's REAL in-progress browser session so the UI can embed the agent's actual
 * screen (what it's browsing right now) instead of a blank throwaway browser. Read-only — it
 * NEVER creates or stops a session, so the caller must NOT stopBrowserUse() on the returned
 * sessionId (that would kill the agent's live browser). `live:false` = no active session yet.
 */
export async function agentBrowserLive(agentId: string): Promise<AgentBrowserLive | null> {
  return getJson<AgentBrowserLive>(
    `/api/morrow/agents/browser/live?agentId=${encodeURIComponent(agentId)}`,
  );
}

export async function fetchAgentRuns(): Promise<readonly AgentRun[]> {
  const res = await getJson<{ runs?: AgentRun[] }>("/api/morrow/agents/runs");
  const mockMode = isAgentsMockMode();
  if (res) agentRunsCache = withMockAgentRuns(res.runs ?? [], mockMode);
  else if (agentRunsCache === null && mockMode) agentRunsCache = withMockAgentRuns([], mockMode);
  return agentRunsCache ?? [];
}

/** A workflow run row (from /api/morrow/workflows/runs) — feeds the Activities feed. */
export interface WorkflowRunLite {
  readonly id: string;
  readonly workflowId: string;
  readonly source: string;
  readonly detail: string | null;
  readonly status: string;
  readonly error: string | null;
  readonly threadId: string | null;
  readonly createdAt: string;
}
export async function fetchWorkflowRuns(): Promise<readonly WorkflowRunLite[]> {
  const res = await getJson<{ runs?: WorkflowRunLite[] }>("/api/morrow/workflows/runs");
  if (res) workflowRunsCache = res.runs ?? [];
  return workflowRunsCache ?? [];
}

/** Just id + name for each of the user's workflows — the chat view's workflow-run header
 * resolves a run's workflowId to a friendly name with this (falls back to the thread title). */
export interface WorkflowSummaryLite {
  readonly id: string;
  readonly name: string;
}
let workflowSummariesCache: readonly WorkflowSummaryLite[] | null = null;
export async function fetchWorkflowSummaries(): Promise<readonly WorkflowSummaryLite[]> {
  // Summaries read id and name only, so ?brief=1 (no graph, truncated instruction), through the
  // shared cache — this is called on panel mounts, not once.
  const res = await cached("/api/morrow/workflows?brief=1", () =>
    getJson<{ workflows?: { id: string; name?: string | null }[] }>(
      "/api/morrow/workflows?brief=1",
    ),
  );
  if (res)
    workflowSummariesCache = (res.workflows ?? []).map((w) => ({
      id: w.id,
      name: (w.name ?? "").trim(),
    }));
  return workflowSummariesCache ?? [];
}

/** The one run that owns this thread (from /api/morrow/workflows/runs?threadId=…) — the
 * chat view uses it to switch on its distinct workflow-run UI. Null when not a run. */
export async function fetchWorkflowRunByThread(threadId: string): Promise<WorkflowRunLite | null> {
  const res = await getJson<{ run?: WorkflowRunLite | null }>(
    `/api/morrow/workflows/runs?threadId=${encodeURIComponent(threadId)}`,
  );
  return res?.run ?? null;
}

/** End a workflow run for good — every step, loop pass and sub-agent. Interrupting the
 * thread's turn alone only ends the CURRENT step; the walk then carries on with the next one.
 * Resolves false when the request didn't land. */
export async function stopWorkflowRun(runId: string): Promise<boolean> {
  const res = await postJson<{ ok?: boolean }>("/api/morrow/workflows/runs/stop", { runId });
  return res?.ok === true;
}

/** One node's progress within a run (from /api/morrow/workflows/runs/steps) — the Runs tab
 * renders these as an ordered stepper. `iteration`/`total` are set only for for-each loops. */
export interface WorkflowRunStepLite {
  readonly id: string;
  readonly nodeId: string;
  readonly kind: string | null;
  readonly label: string | null;
  readonly status: string;
  readonly iteration: number | null;
  readonly total: number | null;
  readonly detail: string | null;
  readonly startedAt: string;
  readonly endedAt: string | null;
  /** Which time round a loop this step ran (branching workflows). */
  readonly pass?: number | null;
  /** The route a router node took. */
  readonly branch?: string | null;
  /** The thread a For each item ran in (its own browser tab), so the run view can open it. */
  readonly threadId?: string | null;
  /** The prompt the step was given — what it was asked, next to what it answered. */
  readonly prompt?: string | null;
}
export async function fetchWorkflowRunSteps(
  runId: string,
): Promise<readonly WorkflowRunStepLite[]> {
  const res = await getJson<{ steps?: WorkflowRunStepLite[] }>(
    `/api/morrow/workflows/runs/steps?runId=${encodeURIComponent(runId)}`,
  );
  return res?.steps ?? [];
}

/** Run a workflow now (a manual test run) — used by the Activities "Run now"/"Retry" actions. */
export async function runWorkflow(id: string): Promise<boolean> {
  const res = await postJson<{ ok?: boolean; started?: boolean }>("/api/morrow/workflows/run", {
    id,
  });
  return Boolean(res?.ok);
}

/** Resume a stopped / failed run in place (same thread) — it skips the items already done. */
export async function resumeWorkflowRun(runId: string): Promise<boolean> {
  const res = await postJson<{ ok?: boolean; started?: boolean }>(
    "/api/morrow/workflows/runs/resume",
    { runId },
  );
  return Boolean(res?.ok);
}

/** A pending approval ("allowance") awaiting the user's decision. */
export interface AllowanceLite {
  readonly id: string;
  readonly action: string;
  readonly title: string;
  readonly threadId: string | null;
  readonly createdAt: string;
}
export async function fetchAllowances(): Promise<readonly AllowanceLite[]> {
  const res = await getJson<{ allowances?: AllowanceLite[] }>("/api/morrow/allowances");
  if (res) allowancesCache = res.allowances ?? [];
  return allowancesCache ?? [];
}

/** One agent-to-agent board message (from /api/morrow/agents/board) — the comms feed. */
export interface AgentBoardMessage {
  readonly id: string;
  readonly channel: string;
  readonly fromAgentId: string;
  readonly toAgentId: string | null;
  readonly subject: string;
  readonly body: string;
  readonly status: string;
  readonly createdAt: string;
}
export async function fetchAgentBoard(): Promise<readonly AgentBoardMessage[]> {
  const res = await getJson<{ messages?: AgentBoardMessage[] }>("/api/morrow/agents/board");
  if (res) agentBoardCache = res.messages ?? [];
  return agentBoardCache ?? [];
}

export async function upsertAgentTag(
  agentId: string,
  handle: string,
  channelScope?: string,
): Promise<boolean> {
  const res = await postJson<{ ok?: boolean }>("/api/morrow/agents/tags", {
    agentId,
    handle,
    ...(channelScope ? { channelScope } : {}),
  });
  return Boolean(res?.ok);
}

export async function deleteAgentTag(handle: string, channelScope?: string): Promise<boolean> {
  const res = await postJson<{ ok?: boolean }>("/api/morrow/agents/tags/delete", {
    handle,
    ...(channelScope ? { channelScope } : {}),
  });
  return Boolean(res?.ok);
}
